import React, { useState, useMemo } from "react";
import { Table, DatePicker, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import { Percent } from "lucide-react";
import dayjs from "dayjs";
import { ReportPage } from "../../../components";
import { GetSalesReportDiscounts } from "../../../Api";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

const { RangePicker } = DatePicker;

export const DiscountsReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);

  const startDateStr = dateRange[0].format("YYYY-MM-DD");
  const endDateStr = dateRange[1].format("YYYY-MM-DD");

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["discounts-report", startDateStr, endDateStr],
    queryFn: () =>
      GetSalesReportDiscounts({
        start_date: startDateStr,
        end_date: endDateStr,
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const subtotal = reportData.reduce(
      (sum: number, item: any) => sum + (item.subtotal || 0),
      0,
    );
    const discount = reportData.reduce(
      (sum: number, item: any) => sum + (item.discount_amount || 0),
      0,
    );
    const netAmount = subtotal - discount;
    const avgDisc = subtotal > 0 ? (discount / subtotal) * 100 : 0;
    return [
      {
        title: "Total Subtotal Value",
        value: formatLKR(subtotal),
        colorClass: "text-gray-600",
      },
      {
        title: "Total Discounts Availed",
        value: formatLKR(discount),
        colorClass: "text-rose-600",
      },
      {
        title: "Net Revenue Received",
        value: formatLKR(netAmount),
        colorClass: "text-emerald-600",
      },
      {
        title: "Average Discount Applied",
        value: `${avgDisc.toFixed(2)}%`,
        colorClass: "text-rose-700",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Sale Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500 font-medium">
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Order Reference",
      dataIndex: "order_no",
      key: "order_no",
      render: (v: string) => (
        <span className="text-xs font-bold text-gray-800">#{v}</span>
      ),
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (v: string) => (
        <span className="text-xs text-gray-655 font-semibold">{v}</span>
      ),
    },
    {
      title: "Order Subtotal",
      dataIndex: "subtotal",
      key: "subtotal",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs text-gray-500 font-medium">
          LKR {v.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Cash Discount Given",
      dataIndex: "discount_amount",
      key: "discount_amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs text-rose-600 font-bold">
          -LKR {v.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Discount %",
      dataIndex: "discount_percent",
      key: "discount_percent",
      align: "right" as const,
      render: (v: number) => (
        <Tag color="error" className="font-bold text-xs">
          {v.toFixed(1)}%
        </Tag>
      ),
    },
    {
      title: "Grand Total Rec'd",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-extrabold text-gray-900">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
  ];

  const filterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Reporting Date Period
        </span>
        <RangePicker
          value={dateRange}
          onChange={(dates) =>
            dates && dates[0] && dates[1] && setDateRange([dates[0], dates[1]])
          }
          allowClear={false}
          className="h-9 rounded-lg border border-gray-200 shadow-sm text-xs font-semibold"
        />
      </div>
    </div>
  );

  return (
    <ReportPage
      title="Discount Report"
      description="Track sales price adjustments, total cash discounts, and percentages."
      icon={<Percent className="w-6 h-6 text-rose-600" />}
      backPath="/system/sales-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) =>
            record.id || record.order_no || Math.random().toString()
          }
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
